import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider,
  Switch,
  FormControlLabel,
  Slider,
  MenuItem,
  Chip,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  Palette as PaletteIcon,
  TextFields as TextFieldsIcon,
  Restore as RestoreIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Block as BlockIcon
, DragIndicator as DragIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon } from '@mui/icons-material';

// ============================
// HTML Generator from config
// ============================
function generateWarningHTML(config, type) {
  const isDue = type === 'due';
  const title = config.title || (isDue ? 'Aviso de Vencimiento de Pago' : 'Servicio Suspendido');
  const subtitle = config.subtitle || (isDue 
    ? 'Le recordamos que su servicio de internet presenta recibos pendientes de pago. Para mantener su conexión activa y sin interrupciones, recuerde regularizar sus facturas.'
    : 'Su servicio de internet ha sido suspendido por facturas impagas. Para reactivar su conexión, por favor regularice su deuda pendiente.');
  const footerNote = config.footerNote || (isDue
    ? '* Le sugerimos pagar antes de su fecha límite de corte para evitar la suspensión definitiva de su servicio.'
    : '* Su servicio será reactivado automáticamente una vez confirmado su pago.');
  const contactText = config.contactText || '¿Ya realizó su pago? Reporte su recibo en el portal o contáctenos al';
  const companyName = config.companyName || 'YinoWISP';
  const companyInitial = config.companyInitial || companyName.charAt(0).toUpperCase();
  const portalUrl = config.portalUrl || 'http://cliente.yinowisp.com';
  const portalButtonText = config.portalButtonText || '💳 Pagar en Portal de Clientes';
  const continueButtonText = config.continueButtonText || (isDue ? '⏭ Continuar navegando' : '');
  const showContinueButton = isDue ? (config.showContinueButton !== false) : false;
  const showPaymentGuide = config.showPaymentGuide !== false;
  const layoutOrder = config.layoutOrder || ['header', 'debt', 'payment', 'actions', 'footer'];
  
  // Colors
  const bgColor = config.bgColor || '#07080d';
  const cardBg = config.cardBg || 'rgba(13, 16, 26, 0.75)';
  const primaryColor = config.primaryColor || '#3b82f6';
  const accentColor = config.accentColor || '#6366f1';
  const textColor = config.textColor || '#f3f4f6';
  const mutedColor = config.mutedColor || '#9ca3af';
  const borderRadius = config.borderRadius || 28;
  const fontSize = config.fontSize || 100;

  const blocks = {
    header: `
    <div class="card-header">
      <div class="logo-container">
        <span class="logo-icon">${companyInitial}</span>
        <span class="logo-text">${companyName}</span>
      </div>
      <h1>${title}</h1>
      <div class="client-greeting">Hola, \{{CLIENT_NAME}}</div>
      <p class="subtitle">${subtitle}</p>
    </div>`,
    
    debt: `
    <div class="block-section">
      <div class="section-title">📅 Detalles de su Deuda</div>
      <div style="background: ${primaryColor}0d; border: 1px solid ${primaryColor}26; border-radius: 16px; padding: 16px; margin-bottom: 20px; display: flex; align-items: center; gap: 14px;">
        <div style="font-size: 2rem; flex-shrink: 0;">${isDue ? 'ℹ️' : '⚠️'}</div>
        <div>
          <h3 style="font-size: 0.95rem; font-weight: 600; color: #e5e7eb; margin: 0 0 4px 0;">${isDue ? 'Aviso Informativo' : 'Servicio Suspendido'}</h3>
          <p style="font-size: 0.8rem; color: var(--text-muted); line-height: 1.4; margin: 0;">${isDue ? 'Puede continuar navegando normalmente después de revisar este aviso.' : 'Su acceso a internet ha sido bloqueado hasta que regularice su deuda.'}</p>
        </div>
      </div>
      <table class="invoice-table">
        <tbody>
          \{{PENDING_INVOICES}}
          <tr class="total-row">
            <td style="color: #ffffff;">Total a Regularizar</td>
            <td></td>
            <td style="color: #ffffff; text-align: right; font-weight: 800; font-size: 1.1rem;">S/ \{{TOTAL_AMOUNT}}</td>
          </tr>
        </tbody>
      </table>
      <p style="margin-top: 15px; font-size: 0.8rem; color: var(--text-muted); line-height: 1.4; font-style: italic;">${footerNote}</p>
    </div>`,

    payment: `
    <div class="block-section">
      <div class="section-title">💳 Cuentas de Pago y Guía</div>
      <div style="max-height: 180px; overflow-y: auto; padding-right: 4px; margin-bottom: 20px; border-bottom: 1px solid rgba(255,255,255,0.05); padding-bottom: 10px;">
        \{{PAYMENT_METHODS}}
      </div>
      ${showPaymentGuide ? `
      <div class="section-title" style="margin-bottom: 10px; font-size: 0.8rem; border-bottom: none; padding-bottom: 0;">📌 ¿Cómo realizar su pago?</div>
      <div class="guide-steps">
        <div class="step-item"><span class="step-badge">1</span><span class="step-text">Realiza la transferencia, depósito o escanea el QR usando nuestras <strong>cuentas oficiales</strong> de arriba.</span></div>
        <div class="step-item"><span class="step-badge">2</span><span class="step-text">Toma una <strong>captura de pantalla o foto clara</strong> del comprobante de pago.</span></div>
        <div class="step-item"><span class="step-badge">3</span><span class="step-text">Haz clic en <strong>"${portalButtonText.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]\s?/, '')}"</strong> abajo para subir tu comprobante.</span></div>
      </div>` : ''}
    </div>`,

    actions: `
    <div class="action-panel">
      <a href="${portalUrl}" class="btn btn-portal"><span>${portalButtonText}</span></a>
      ${showContinueButton ? `<button class="btn btn-bypass" id="action-btn"><span id="btn-text">${continueButtonText}</span></button>` : ''}
    </div>`,
    
    footer: `
    <div class="footer-info">${contactText} <a href="tel:\{{COMPANY_PHONE}}">\{{COMPANY_PHONE}}</a>.</div>
    `
  };

  const bodyContent = layoutOrder.map(key => blocks[key] || '').join('\n');

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title} - ${companyName}</title>
  <style>
    :root {
      --bg-dark: ${bgColor};
      --bg-card: ${cardBg};
      --primary: ${primaryColor};
      --primary-hover: ${primaryColor};
      --primary-glow: ${primaryColor}59;
      --accent: ${accentColor};
      --accent-glow: ${accentColor}33;
      --text-main: ${textColor};
      --text-muted: ${mutedColor};
      --border-color: ${primaryColor}26;
      --success: #10b981;
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      background-color: var(--bg-dark);
      color: var(--text-main);
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      min-height: 100vh;
      display: flex; align-items: center; justify-content: center;
      padding: 20px; overflow-x: hidden; position: relative;
      font-size: ${fontSize}%;
    }
    .bg-blob { position: absolute; width: 600px; height: 600px; border-radius: 50%; filter: blur(140px); z-index: -1; opacity: 0.15; animation: floatBlobs 20s infinite alternate; }
    .blob-1 { background: var(--primary); top: -150px; left: -150px; }
    .blob-2 { background: var(--accent); bottom: -200px; right: -150px; animation-delay: -5s; }
    @keyframes floatBlobs { 0% { transform: translate(0, 0) scale(1); } 100% { transform: translate(80px, 50px) scale(1.2); } }
    
    .card {
      background: var(--bg-card);
      backdrop-filter: blur(24px);
      border: 1px solid var(--border-color);
      border-radius: ${borderRadius}px;
      padding: 35px 40px;
      width: 100%;
      max-width: 820px;
      box-shadow: 0 25px 50px -12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1);
      z-index: 10;
      display: flex;
      flex-direction: column;
      gap: 24px;
    }
    
    @media (max-width: 768px) { .card { padding: 25px 20px; } }
    .card-header { text-align: center; }
    .logo-container { display: inline-flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.05); padding: 12px 24px; border-radius: 20px; margin-bottom: 24px; gap: 12px; }
    .logo-icon { background: linear-gradient(135deg, var(--primary), var(--accent)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-size: 2rem; font-weight: 900; line-height: 1; }
    .logo-text { font-size: 1.3rem; font-weight: 700; color: #fff; letter-spacing: 0.5px; }
    h1 { font-size: 2.2rem; font-weight: 800; margin-bottom: 12px; color: #fff; letter-spacing: -0.5px; }
    .client-greeting { color: var(--primary); font-size: 1.25rem; font-weight: 600; margin-bottom: 16px; }
    .subtitle { color: var(--text-muted); font-size: 0.95rem; line-height: 1.6; max-width: 580px; margin: 0 auto; }
    .block-section { background: rgba(0,0,0,0.2); border-radius: 20px; padding: 24px; border: 1px solid rgba(255,255,255,0.03); display: flex; flex-direction: column; }
    .section-title { font-size: 1.05rem; font-weight: 700; color: #fff; margin-bottom: 18px; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid rgba(255,255,255,0.05); padding-bottom: 12px; }
    .invoice-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; margin-top: 10px; }
    .invoice-table td { padding: 12px 0; }
    .total-row { border-top: 1px solid rgba(255,255,255,0.08); font-size: 1.05rem; font-weight: 700; }
    .total-row td { padding: 18px 0 0 0 !important; }
    .guide-steps { display: flex; flex-direction: column; gap: 15px; margin-top: 15px; }
    .step-item { display: flex; align-items: flex-start; gap: 12px; background: rgba(255,255,255,0.01); border-radius: 10px; padding: 10px; }
    .step-badge { background: linear-gradient(135deg, var(--accent), #4f46e5); color: #fff; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.75rem; flex-shrink: 0; }
    .step-text { font-size: 0.82rem; color: var(--text-muted); line-height: 1.4; }
    .step-text strong { color: #e5e7eb; }
    .action-panel { display: grid; grid-template-columns: 1fr; gap: 16px; max-width: 720px; margin: 0 auto; width: 100%; }
    @media (min-width: 576px) { .action-panel { grid-template-columns: ${showContinueButton ? '1fr 1fr' : '1fr'}; } }
    .btn { width: 100%; padding: 16px 28px; font-size: 1.02rem; font-weight: 700; border-radius: 14px; cursor: pointer; transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1); display: flex; align-items: center; justify-content: center; gap: 10px; font-family: inherit; text-decoration: none; }
    .btn-portal { background: linear-gradient(135deg, var(--primary), ${primaryColor}cc); border: 1px solid rgba(255,255,255,0.1); color: #fff; box-shadow: 0 10px 20px var(--primary-glow); }
    .btn-portal:hover { transform: translateY(-2px); box-shadow: 0 14px 28px var(--primary-glow); filter: brightness(1.1); }
    .btn-bypass { background: linear-gradient(135deg, var(--accent), #4f46e5); border: 1px solid rgba(255,255,255,0.12); color: #fff; cursor: pointer; box-shadow: 0 10px 20px var(--accent-glow); }
    .btn-bypass:hover { transform: translateY(-2px); box-shadow: 0 14px 28px var(--accent-glow); filter: brightness(1.15); }
    .footer-info { text-align: center; margin-top: 8px; font-size: 0.85rem; color: var(--text-muted); }
    .footer-info a { color: var(--primary); text-decoration: none; font-weight: 600; }
    .clickable-payment-card { transition: all 0.25s ease-in-out !important; }
    .clickable-payment-card:hover { background: ${primaryColor}14 !important; border-color: ${primaryColor}4d !important; transform: translateY(-2px); }
  </style>
</head>
<body>
  <div class="bg-blob blob-1"></div>
  <div class="bg-blob blob-2"></div>
  <div class="card">
    ${bodyContent}
  </div>
  <script>
    document.addEventListener('DOMContentLoaded', () => {
      const actionBtn = document.getElementById('action-btn');
      if (actionBtn) {
        actionBtn.addEventListener('click', async () => {
          actionBtn.setAttribute('disabled', 'true');
          document.getElementById('btn-text').textContent = 'Descartando aviso...';
          try {
            const urlParams = new URLSearchParams(window.location.search);
            const customIp = urlParams.get('ip');
            const bypassUrl = customIp ? '/api/clients/warning-bypass?ip=' + customIp : '/api/clients/warning-bypass';
            const response = await fetch(bypassUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
            const result = await response.json();
            if (response.ok && result.success) {
              document.getElementById('btn-text').textContent = '¡Internet activo!';
              actionBtn.style.background = '#10b981';
              setTimeout(() => { window.location.href = result.redirectUrl || 'https://www.google.com'; }, 1200);
            } else { throw new Error(result.message || 'Error'); }
          } catch (e) { document.getElementById('btn-text').textContent = 'Reintentar'; actionBtn.removeAttribute('disabled'); alert('Error: ' + e.message); }
        });
      }
    });
    function showQrModal(name, qrUrl) { const m = document.getElementById('qr-modal'); const t = document.getElementById('qr-modal-title'); const i = document.getElementById('qr-modal-image'); const c = m.querySelector('.modal-card'); t.textContent = 'Código QR: ' + name; i.src = qrUrl; m.style.opacity = '1'; m.style.pointerEvents = 'auto'; c.style.transform = 'scale(1)'; }
    function closeQrModal() { const m = document.getElementById('qr-modal'); const c = m.querySelector('.modal-card'); m.style.opacity = '0'; m.style.pointerEvents = 'none'; c.style.transform = 'scale(0.95)'; }
  </script>
  <div id="qr-modal" style="position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(7,8,13,0.85);backdrop-filter:blur(20px);display:flex;align-items:center;justify-content:center;z-index:9999;opacity:0;pointer-events:none;transition:all 0.3s" onclick="closeQrModal()">
    <div class="modal-card" style="background:rgba(20,24,38,0.95);border:1px solid ${primaryColor}40;border-radius:24px;padding:30px;width:90%;max-width:400px;text-align:center;box-shadow:0 30px 60px rgba(0,0,0,0.8);transform:scale(0.95);transition:all 0.3s" onclick="event.stopPropagation()">
      <h3 id="qr-modal-title" style="font-size:1.4rem;margin-bottom:8px;color:#fff;font-weight:700;">Código QR</h3>
      <p style="color:var(--text-muted);font-size:0.85rem;margin-bottom:20px;line-height:1.4;">Escanea el código con tu celular desde la app de tu banco.</p>
      <div style="background:#fff;border-radius:16px;padding:15px;display:inline-block;margin-bottom:20px;">
        <img id="qr-modal-image" src="" alt="QR" style="width:220px;height:220px;display:block;object-fit:contain;" />
      </div>
      <button style="background:linear-gradient(135deg,var(--primary),${primaryColor}cc);border:none;color:#fff;width:100%;padding:12px 20px;font-size:0.95rem;font-weight:700;border-radius:12px;cursor:pointer;" onclick="closeQrModal()">Cerrar</button>
    </div>
  </div>
</body>
</html>`;
}
// ============================
// Default configs
// ============================
// Default configs
// ============================
const defaultDueConfig = {
  title: 'Aviso de Vencimiento de Pago',
  subtitle: 'Le recordamos que su servicio de internet presenta recibos pendientes de pago. Para mantener su conexión activa y sin interrupciones, recuerde regularizar sus facturas.',
  footerNote: '* Le sugerimos pagar antes de su fecha límite de corte para evitar la suspensión definitiva de su servicio.',
  contactText: '¿Ya realizó su pago? Reporte su recibo en el portal o contáctenos al',
  companyName: 'YinoWISP',
  companyInitial: 'Y',
  portalUrl: 'http://cliente.yinowisp.com',
  portalButtonText: '📲 Pagar en Portal de Clientes',
  continueButtonText: '✅ Continuar navegando',
  showContinueButton: true,
  showPaymentGuide: true,
  bgColor: '#07080d',
  cardBg: 'rgba(13, 16, 26, 0.75)',
  primaryColor: '#3b82f6',
  accentColor: '#6366f1',
  textColor: '#f3f4f6',
  mutedColor: '#9ca3af',
  borderRadius: 28,
  fontSize: 100,
  layoutOrder: ['header', 'debt', 'payment', 'actions', 'footer']
};

const defaultCutConfig = {
  title: 'Servicio Suspendido',
  subtitle: 'Su servicio de internet ha sido suspendido por facturas impagas. Para reactivar su conexión, por favor regularice su deuda pendiente.',
  footerNote: '* Su servicio será reactivado automáticamente una vez confirmado su pago.',
  contactText: '¿Ya realizó su pago? Reporte su recibo en el portal o contáctenos al',
  companyName: 'YinoWISP',
  companyInitial: 'Y',
  portalUrl: 'http://cliente.yinowisp.com',
  portalButtonText: '📲 Pagar en Portal de Clientes',
  continueButtonText: '',
  showContinueButton: false,
  showPaymentGuide: true,
  bgColor: '#07080d',
  cardBg: 'rgba(13, 16, 26, 0.75)',
  primaryColor: '#ef4444',
  accentColor: '#dc2626',
  textColor: '#f3f4f6',
  mutedColor: '#9ca3af',
  borderRadius: 28,
  fontSize: 100,
  layoutOrder: ['header', 'debt', 'payment', 'actions', 'footer']
};

// ============================
// Component
// ============================
function WarningDesigner() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dueConfig, setDueConfig] = useState({ ...defaultDueConfig });
  const [cutConfig, setCutConfig] = useState({ ...defaultCutConfig });
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  // Fetch existing config from server (stored as JSON in a finance_setting key)
  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/finance/settings', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.warning_due_config) {
          try { setDueConfig({ ...defaultDueConfig, ...JSON.parse(data.warning_due_config) }); } catch (e) { /* use defaults */ }
        }
        if (data.warning_cut_config) {
          try { setCutConfig({ ...defaultCutConfig, ...JSON.parse(data.warning_cut_config) }); } catch (e) { /* use defaults */ }
        }
      }
    } catch (error) {
      console.error('Error fetching warning configs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleSave = async (type) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = type === 'due' ? dueConfig : cutConfig;
      const html = generateWarningHTML(config, type);

      // Save config JSON to finance_settings
      const configKey = type === 'due' ? 'warning_due_config' : 'warning_cut_config';
      await fetch('/api/finance/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ [configKey]: JSON.stringify(config) })
      });

      // Save generated HTML template
      const response = await fetch('/api/system/warnings/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ type, content: html })
      });

      if (response.ok) {
        showToast(`Plantilla de ${type === 'due' ? 'Vencimiento' : 'Corte'} guardada correctamente`, 'success');
      } else {
        showToast('Error al guardar', 'error');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      showToast('Error de conexión', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = (type) => {
    if (type === 'due') setDueConfig({ ...defaultDueConfig });
    else setCutConfig({ ...defaultCutConfig });
    showToast('Configuración restablecida a valores por defecto', 'info');
  };

  const currentConfig = activeTab === 0 ? dueConfig : cutConfig;
  const setCurrentConfig = activeTab === 0 ? setDueConfig : setCutConfig;
  const currentType = activeTab === 0 ? 'due' : 'cut';

  const updateField = (field, value) => {
    setCurrentConfig(prev => ({ ...prev, [field]: value }));
  };

  const previewHtml = generateWarningHTML(currentConfig, currentType);

  const renderConfigPanel = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Organizador Visual */}
      <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: '#f8fafc' }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <DragIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="bold">Organizador Visual (Arrastrar y Soltar)</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Arrastra los bloques para cambiar el orden en que aparecen en el aviso, o usa las flechas de subir/bajar.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {currentConfig.layoutOrder && currentConfig.layoutOrder.map((key, index) => {
              const blockNames = {
                header: 'Encabezado (Logo y Título)',
                debt: 'Detalles de Deuda',
                payment: 'Cuentas y Guía de Pago',
                actions: 'Botones de Acción',
                footer: 'Pie de página (Contacto)'
              };
              return (
                <Paper 
                  key={key} 
                  elevation={0}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', index);
                    e.currentTarget.style.opacity = '0.5';
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
                    const toIdx = index;
                    if (isNaN(fromIdx) || fromIdx === toIdx) return;
                    const newOrder = [...currentConfig.layoutOrder];
                    const [moved] = newOrder.splice(fromIdx, 1);
                    newOrder.splice(toIdx, 0, moved);
                    updateField('layoutOrder', newOrder);
                  }}
                  sx={{ 
                    p: 1.5, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    bgcolor: '#fff', 
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                    '&:hover': { borderColor: '#cbd5e1', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }
                  }}
                >
                  <DragIcon sx={{ color: '#94a3b8' }} />
                  <Typography variant="body2" fontWeight="500" sx={{ flex: 1 }}>{blockNames[key]}</Typography>
                  <Box display="flex" gap={0.5}>
                    <IconButton 
                      size="small" 
                      disabled={index === 0}
                      onClick={() => {
                        const newOrder = [...currentConfig.layoutOrder];
                        const [moved] = newOrder.splice(index, 1);
                        newOrder.splice(index - 1, 0, moved);
                        updateField('layoutOrder', newOrder);
                      }}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      disabled={index === currentConfig.layoutOrder.length - 1}
                      onClick={() => {
                        const newOrder = [...currentConfig.layoutOrder];
                        const [moved] = newOrder.splice(index, 1);
                        newOrder.splice(index + 1, 0, moved);
                        updateField('layoutOrder', newOrder);
                      }}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* Textos */}
      <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: '#f8fafc' }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <TextFieldsIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="bold">Textos y Contenido</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Nombre de la Empresa" value={currentConfig.companyName} onChange={(e) => updateField('companyName', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Inicial/Logo Texto" value={currentConfig.companyInitial} onChange={(e) => updateField('companyInitial', e.target.value)} helperText="Letra o emoji que aparece como logo" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Título Principal" value={currentConfig.title} onChange={(e) => updateField('title', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Mensaje / Descripción" multiline rows={3} value={currentConfig.subtitle} onChange={(e) => updateField('subtitle', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Nota al pie (bajo la tabla de deuda)" value={currentConfig.footerNote} onChange={(e) => updateField('footerNote', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Texto de contacto (pie de página)" value={currentConfig.contactText} onChange={(e) => updateField('contactText', e.target.value)} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Botones */}
      <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: '#f8fafc' }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <InfoIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="bold">Botones y Acciones</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="URL del Portal de Clientes" value={currentConfig.portalUrl} onChange={(e) => updateField('portalUrl', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Texto del Botón de Pago" value={currentConfig.portalButtonText} onChange={(e) => updateField('portalButtonText', e.target.value)} />
            </Grid>
            {activeTab === 0 && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch checked={currentConfig.showContinueButton !== false} onChange={(e) => updateField('showContinueButton', e.target.checked)} />}
                    label="Mostrar botón 'Continuar navegando'"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Texto del Botón Continuar" value={currentConfig.continueButtonText} onChange={(e) => updateField('continueButtonText', e.target.value)} disabled={!currentConfig.showContinueButton} />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={currentConfig.showPaymentGuide !== false} onChange={(e) => updateField('showPaymentGuide', e.target.checked)} />}
                label="Mostrar guía de pasos para pagar"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Colores */}
      <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: '#f8fafc' }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <PaletteIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="bold">Colores y Estilo</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="textSecondary">Fondo</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <input type="color" value={currentConfig.bgColor} onChange={(e) => updateField('bgColor', e.target.value)} style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }} />
                <Typography variant="caption">{currentConfig.bgColor}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="textSecondary">Color Primario</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <input type="color" value={currentConfig.primaryColor} onChange={(e) => updateField('primaryColor', e.target.value)} style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }} />
                <Typography variant="caption">{currentConfig.primaryColor}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="textSecondary">Color Acento</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <input type="color" value={currentConfig.accentColor} onChange={(e) => updateField('accentColor', e.target.value)} style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }} />
                <Typography variant="caption">{currentConfig.accentColor}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="textSecondary">Texto Principal</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <input type="color" value={currentConfig.textColor} onChange={(e) => updateField('textColor', e.target.value)} style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }} />
                <Typography variant="caption">{currentConfig.textColor}</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="caption" color="textSecondary">Texto Secundario</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <input type="color" value={currentConfig.mutedColor} onChange={(e) => updateField('mutedColor', e.target.value)} style={{ width: 36, height: 36, border: 'none', borderRadius: 8, cursor: 'pointer' }} />
                <Typography variant="caption">{currentConfig.mutedColor}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="textSecondary" gutterBottom>Redondez de Bordes: {currentConfig.borderRadius}px</Typography>
              <Slider value={currentConfig.borderRadius} onChange={(e, v) => updateField('borderRadius', v)} min={0} max={40} step={2} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="textSecondary" gutterBottom>Tamaño de Fuente: {currentConfig.fontSize}%</Typography>
              <Slider value={currentConfig.fontSize} onChange={(e, v) => updateField('fontSize', v)} min={80} max={120} step={5} size="small" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Variables Info */}
      <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: '#fff3cd', border: '1px solid #ffc107' }}>
        <CardContent sx={{ py: 1.5 }}>
          <Typography variant="caption" fontWeight="bold" color="text.primary">
            ℹ️ Las variables dinámicas se insertan automáticamente: nombre del cliente, facturas, monto total, métodos de pago y teléfono de contacto.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
        <Typography mt={2}>Cargando configuración...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1400px', mx: 'auto', p: { xs: 1, sm: 2, md: 3 } }}>
      <Box mb={3} display="flex" alignItems="center" gap={2}>
        <Box sx={{ bgcolor: 'warning.light', p: 1.5, borderRadius: 2, color: 'warning.main', display: 'flex' }}>
          <WarningIcon fontSize="large" />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight="bold">Diseñador de Avisos en Pantalla</Typography>
          <Typography variant="body2" color="textSecondary">
            Personaliza los avisos que ven tus clientes cuando tienen facturas pendientes o servicio suspendido.
          </Typography>
        </Box>
      </Box>

      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc' }}
        >
          <Tab icon={<InfoIcon fontSize="small" />} iconPosition="start" label="Aviso Preventivo (Vencimiento)" />
          <Tab icon={<BlockIcon fontSize="small" />} iconPosition="start" label="Aviso de Corte (Suspensión)" />
        </Tabs>

        <Box p={{ xs: 2, sm: 3 }}>
          <Grid container spacing={3}>
            {/* Left: Config Panel */}
            <Grid item xs={12} md={5}>
              {renderConfigPanel()}
              <Box mt={2} display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={() => handleSave(currentType)}
                  disabled={saving}
                  sx={{ flex: 1, textTransform: 'none', fontWeight: 'bold', borderRadius: 2 }}
                >
                  {saving ? 'Guardando...' : 'Guardar y Publicar'}
                </Button>
                <Tooltip title="Restablecer valores por defecto">
                  <IconButton onClick={() => handleReset(currentType)} color="warning">
                    <RestoreIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>

            {/* Right: Live Preview */}
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', height: '100%', minHeight: 600, display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" alignItems="center" p={1.5} borderBottom="1px solid #e0e0e0" bgcolor="#f5f5f5" gap={1}>
                  <VisibilityIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                  <Typography variant="subtitle2" color="text.secondary">Vista Previa en Vivo</Typography>
                  <Chip label="Los datos reales se llenan automáticamente" size="small" variant="outlined" sx={{ ml: 'auto', fontSize: '0.7rem' }} />
                </Box>
                <Box flexGrow={1} sx={{ position: 'relative', bgcolor: '#07080d' }}>
                  <iframe
                    srcDoc={previewHtml}
                    title="Preview"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 'none'
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default WarningDesigner;
